/**
 * Simulación antes de guardar una regla de recargo (2026-09-18).
 * Muestra cuántos avisos cambian y cuánto, por banda, y ofrece:
 *  - Iniciar piloto (10% de los productos por 72 h) — recomendado
 *  - Aplicar a todos ahora — con advertencia
 * Origen: el 2/9 un cambio de 7→10 USD se aplicó a 10.028 avisos de golpe y las ventas cayeron a la mitad.
 */
import { AlertTriangle, FlaskConical, Zap } from 'lucide-react';

export interface BandSim {
  band: string;
  items: number;
  changed: number;
  avg_pct_change: number | null;
  max_pct_change: number | null;
  avg_old_price: number | null;
  avg_new_price: number | null;
  risk: 'ninguno' | 'moderado' | 'alto';
}

export interface Simulation {
  total_items: number;
  changed_items: number;
  bands: BandSim[];
  warnings: string[];
}

interface Props {
  sim: Simulation | null;
  loading: boolean;
  markupChanged: boolean;
  saving: boolean;
  pilotPct: number;
  onPilotPct: (n: number) => void;
  onPilot: () => void;
  onApplyAll: () => void;
  onCancel: () => void;
}

const riskClass: Record<BandSim['risk'], string> = {
  ninguno: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  moderado: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  alto: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

export function MarkupSimulationModal(p: Props) {
  const hasHigh = p.sim?.bands.some((b) => b.risk === 'alto');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-xl space-y-4">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-text)]">Antes de guardar: qué va a pasar con los precios</h3>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Estimación sobre los productos activos de esta cuenta con costo Amazon conocido.
          </p>
        </div>

        {p.loading && <div className="text-sm text-[var(--color-text-muted)]">Calculando…</div>}

        {p.sim && (
          <>
            <div className="text-sm text-[var(--color-text)]">
              Cambian <b>{p.sim.changed_items}</b> de {p.sim.total_items} productos.
            </div>
            <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--color-bg)] border-b border-[var(--color-border)] text-[var(--color-text-muted)]">
                    <th className="text-left px-3 py-2 font-medium">Banda (costo Amazon)</th>
                    <th className="text-right px-3 py-2 font-medium">Avisos</th>
                    <th className="text-right px-3 py-2 font-medium">Cambian</th>
                    <th className="text-right px-3 py-2 font-medium">Suba promedio</th>
                    <th className="text-right px-3 py-2 font-medium">Precio prom. antes → después</th>
                    <th className="text-center px-3 py-2 font-medium">Riesgo</th>
                  </tr>
                </thead>
                <tbody>
                  {p.sim.bands.map((b) => (
                    <tr key={b.band} className="border-b border-[var(--color-border)]">
                      <td className="px-3 py-2 font-mono text-[var(--color-text)]">{b.band}</td>
                      <td className="px-3 py-2 text-right">{b.items}</td>
                      <td className="px-3 py-2 text-right">{b.changed}</td>
                      <td className="px-3 py-2 text-right">
                        {b.avg_pct_change == null ? '—' : `${b.avg_pct_change > 0 ? '+' : ''}${b.avg_pct_change}%`}
                        {b.max_pct_change != null && b.max_pct_change !== b.avg_pct_change && (
                          <span className="text-xs text-[var(--color-text-muted)]"> (hasta {b.max_pct_change > 0 ? '+' : ''}{b.max_pct_change}%)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {b.avg_old_price == null ? '—' : `$${b.avg_old_price}`} → {b.avg_new_price == null ? '—' : `$${b.avg_new_price}`}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${riskClass[b.risk]}`}>{b.risk}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {p.sim.warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-xs text-red-800 dark:text-red-200">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {w}
              </div>
            ))}
          </>
        )}

        {p.markupChanged ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-[var(--color-surface-hover)] border border-[var(--color-border)] text-xs text-[var(--color-text-secondary)]">
              <b>Recomendado:</b> aplicar primero a una parte de los productos durante 72 horas. El sistema compara ventas y visitas
              de esa parte contra el resto y te avisa con una recomendación. Después decidís si extender a todos o volver atrás.
              <div className="mt-2 flex items-center gap-2">
                <label className="text-xs">Porcentaje del piloto</label>
                <input type="number" min={1} max={100} value={p.pilotPct} onChange={(e) => p.onPilotPct(Number(e.target.value) || 10)}
                  className="w-20 px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm" />
                <span className="text-xs">%</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={p.onCancel} className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)]">Cancelar</button>
              <button onClick={p.onApplyAll} disabled={p.saving}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border ${hasHigh ? 'border-red-400 text-red-700 dark:text-red-300' : 'border-[var(--color-border)] text-[var(--color-text-secondary)]'} disabled:opacity-50`}
                title="Aplica el valor nuevo a todos los productos del rango de inmediato">
                <Zap size={14} /> Aplicar a todos ahora
              </button>
              <button onClick={p.onPilot} disabled={p.saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50">
                <FlaskConical size={14} /> {p.saving ? 'Guardando…' : `Iniciar piloto ${p.pilotPct}% (72 h)`}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <button onClick={p.onCancel} className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)]">Cancelar</button>
            <button onClick={p.onApplyAll} disabled={p.saving}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50">
              {p.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
